// Right-drag axis-snap orbit — shared by the 2D floor plan and the model's
// orthographic view. The drag turns an internal "free" orientation (trackball,
// so it responds from any view); whenever it crosses into a new nearest-axis
// region the camera briefly tweens to that axis-aligned view — the same quick
// "dip through 3D" as clicking an axis on the gizmo, and a fast flick is one
// discrete 90° step. The view only ever settles square to an axis. Middle-drag
// still pans. The config adapts it to each camera / controls / axis set.

import * as THREE from "three";
import { nearestAxisIn, planUpForAxis } from "../view/axis-math";

export interface AxisSnapConfig {
  isActive: () => boolean;
  axes: THREE.Vector3[];
  target: () => THREE.Vector3; // orbit pivot
  eyePos: () => THREE.Vector3; // current camera position
  camUp: () => THREE.Vector3; // current camera up
  // Tween the camera to look down `axis`. `up` (when given) is the roll to keep.
  snap: (axis: THREE.Vector3, up?: THREE.Vector3) => void;
  cancelTween: () => void; // abort any in-flight snap
  // Resolve the up-vector when snapping onto `axis`, given the current rolled
  // `currentUp`. Fill `out` and return true to keep that roll; return false (or
  // omit) for the canonical (roll-reset) up. Lets a config preserve roll on some
  // axes and reset on others (e.g. the plan keeps the top view's roll but forces
  // side elevations upright).
  resolveUp?: (
    axis: THREE.Vector3,
    currentUp: THREE.Vector3,
    out: THREE.Vector3,
  ) => boolean;
}

export function installAxisSnapOrbit(dom: HTMLElement, cfg: AxisSnapConfig): void {
  const ROT_SPEED = (2 * Math.PI) / 4200; // rad/px; ~1050px of drag per 90°
  const ORBIT_THRESH = 36; // px of drag before the orbit starts turning
  const FLICK_SPEED = 1.5; // px/ms; above this a swipe is one discrete 90° step
  const _eye = new THREE.Vector3(); // free view dir (target → camera), unit
  const _up = new THREE.Vector3(); // free up
  const _right = new THREE.Vector3();
  const _q = new THREE.Quaternion();
  const _q2 = new THREE.Quaternion();
  const _upTmp = new THREE.Vector3();
  let active = false;
  let armed = false; // true once the drag has cleared the dead-zone threshold
  let flickDone = false; // a flick already stepped; wait for the speed to drop
  let committed = false; // one axis change per press — the rest of the drag is idle
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let lastY = 0;
  let lastT = 0; // timestamp of the last move, for the swipe-speed estimate
  let lastAxis: THREE.Vector3 | null = null; // axis currently tweened-to

  const nearest = (d: THREE.Vector3): THREE.Vector3 => nearestAxisIn(d, cfg.axes);

  // Tween to `axis` if it's newly nearest, then re-center the free orientation
  // ONTO it. Without the re-center the orientation would sit on the 45° boundary
  // and tiny jitter would keep flipping the axis; this way the next change needs
  // a fresh ~quarter turn in either direction.
  const commitAxis = (axis: THREE.Vector3): void => {
    if (axis === lastAxis) return;
    if (cfg.resolveUp && cfg.resolveUp(axis, _up, _upTmp)) {
      // Carry a roll over (the resolver filled _upTmp with the up to keep).
      cfg.snap(axis, _upTmp);
      _up.copy(_upTmp);
    } else {
      cfg.snap(axis);
      planUpForAxis(axis, _up);
    }
    lastAxis = axis;
    _eye.copy(axis);
    committed = true; // one swipe per click — ignore the rest of this drag
  };

  // One discrete 90° step from the current axis in the swipe's dominant
  // direction — used for a fast flick, so distance doesn't matter, only that it
  // was quick. Snaps to the current axis first so the step is always clean.
  const flickStep = (dx: number, dy: number): void => {
    const from = nearest(_eye);
    _eye.copy(from);
    // Keep the current roll (resolver) or reset to canonical before stepping.
    if (cfg.resolveUp && cfg.resolveUp(from, _up, _upTmp)) {
      _up.copy(_upTmp);
    } else {
      planUpForAxis(from, _up);
    }
    _right.crossVectors(_up, _eye).normalize();
    if (Math.abs(dx) >= Math.abs(dy)) {
      _q.setFromAxisAngle(_up, -Math.sign(dx) * (Math.PI / 2)); // horizontal
    } else {
      _q.setFromAxisAngle(_right, -Math.sign(dy) * (Math.PI / 2)); // vertical
    }
    _eye.applyQuaternion(_q).normalize();
    _up.applyQuaternion(_q).normalize();
    commitAxis(nearest(_eye));
  };

  dom.addEventListener("contextmenu", (e) => {
    if (cfg.isActive()) e.preventDefault();
  });
  dom.addEventListener("pointerdown", (e) => {
    if (e.button !== 2 || !cfg.isActive()) return; // right button only
    active = true;
    armed = false;
    flickDone = false;
    committed = false;
    cfg.cancelTween(); // cancel any in-flight snap
    // Seed the free orientation from the current (axis-aligned) view.
    _eye.copy(cfg.eyePos()).sub(cfg.target()).normalize();
    _up.copy(cfg.camUp());
    lastAxis = nearest(_eye); // already here; don't re-tween to it
    startX = lastX = e.clientX;
    startY = lastY = e.clientY;
    lastT = e.timeStamp;
  });
  window.addEventListener("pointermove", (e) => {
    if (!active || committed) return; // one swipe per click
    // Dead-zone: ignore small drags so the axis doesn't change on a near-click.
    // Once cleared, re-anchor to the current point so there's no sudden jump.
    if (!armed) {
      if (Math.hypot(e.clientX - startX, e.clientY - startY) <= ORBIT_THRESH)
        return;
      armed = true;
      lastX = e.clientX;
      lastY = e.clientY;
      lastT = e.timeStamp;
    }
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    const dt = Math.max(1, e.timeStamp - lastT); // ms; guard against 0
    lastX = e.clientX;
    lastY = e.clientY;
    lastT = e.timeStamp;
    // Apple-swipe feel: a quick flick is ONE discrete 90° step (however far it
    // travels), while a slow drag rotates continuously by distance. Speed picks.
    if (Math.hypot(dx, dy) / dt >= FLICK_SPEED) {
      if (!flickDone) {
        flickStep(dx, dy);
        flickDone = true; // swallow the rest of the fast motion → one step
      }
      return;
    }
    flickDone = false; // slow again → allow the next flick and rotate live
    // Rotate the free orientation (camera local X = up × eye = screen-right).
    _right.crossVectors(_up, _eye).normalize();
    _q.setFromAxisAngle(_up, -dx * ROT_SPEED); // horizontal → spin
    _q2.setFromAxisAngle(_right, -dy * ROT_SPEED); // vertical → tilt
    _q.multiply(_q2);
    _eye.applyQuaternion(_q).normalize();
    _up.applyQuaternion(_q).normalize();
    commitAxis(nearest(_eye));
  });
  window.addEventListener("pointerup", (e) => {
    if (e.button !== 2 || !active) return;
    active = false; // the in-flight tween (if any) finishes on an axis view
  });
}
